import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowDown, X, ExternalLink } from "lucide-react";
import { useI18n } from "../../lib/i18n";

const URL_RE = /https?:\/\/[^\s]+/;

export function videoEmbed(url) {
  let u;
  try { u = new URL(url); } catch { return null; }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const host = u.hostname.replace(/^www\.|^m\./, "");
  let id = null;
  if (host === "youtu.be") id = u.pathname.slice(1);
  else if (host === "youtube.com") id = u.searchParams.get("v") || (u.pathname.match(/^\/(shorts|embed|live)\/([^/?]+)/) || [])[2];
  if (id && /^[\w-]{6,20}$/.test(id)) return { kind: "iframe", src: `https://www.youtube-nocookie.com/embed/${id}` };
  const drive = host === "drive.google.com" && u.pathname.match(/\/file\/d\/([\w-]+)/);
  if (drive) return { kind: "iframe", src: `https://drive.google.com/file/d/${drive[1]}/preview` };
  if (/\.(mp4|webm|mov|m4v)$/i.test(u.pathname)) return { kind: "video", src: u.href };
  return null;
}

function VideoBlock({ url, mine, linkInText }) {
  const emb = videoEmbed(url);
  return (
    <div data-testid="chat-video-block" className="mb-1 space-y-1">
      {emb?.kind === "iframe" && <iframe data-testid="chat-video-embed" src={emb.src} title="video" className="w-full sm:w-80 aspect-video rounded-sm bg-black" allow="encrypted-media; picture-in-picture; fullscreen" allowFullScreen sandbox="allow-scripts allow-same-origin allow-presentation allow-popups" referrerPolicy="strict-origin-when-cross-origin" />}
      {emb?.kind === "video" && <video data-testid="chat-video-embed" src={emb.src} controls preload="metadata" className="w-full sm:w-80 rounded-sm bg-black" />}
      {!linkInText && <a data-testid="chat-video-link" href={url} target="_blank" rel="noopener noreferrer nofollow" className={`inline-flex items-center gap-1 underline break-all text-xs ${mine ? "text-[#101114]" : "text-[#F2D98D]"}`}><ExternalLink size={12} className="shrink-0" /> {url}</a>}
    </div>
  );
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div data-testid="chat-lightbox" onClick={onClose} className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
      <button data-testid="chat-lightbox-close" onClick={onClose} className="absolute top-4 right-4 bg-[#101114] border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#101114] rounded-full p-2 transition-colors"><X size={20} /></button>
      <img data-testid="chat-lightbox-img" src={src} alt="preview" onClick={(e) => e.stopPropagation()} className="max-w-full max-h-[90vh] object-contain rounded-sm shadow-2xl" />
    </div>
  );
}

function renderText(text) {
  const re = /(https?:\/\/[^\s]+)/g;
  return text.split(re).map((part, i) => (i % 2 === 1
    ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline text-[#F2D98D]">{part}</a>
    : <span key={i}>{part}</span>));
}

function Bubble({ m, mine, onZoom }) {
  const textVideo = !m.video_url && m.text && (m.text.match(URL_RE) || [])[0];
  const vid = m.video_url || (textVideo && videoEmbed(textVideo) ? textVideo : null);
  return (
    <div data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] rounded-lg px-4 py-2 ${mine ? "bg-[#D4AF37] text-[#101114]" : "bg-[#202127] text-[#F5F3ED] border border-[#35353B]"}`}>
        {!mine && <div className="text-[10px] opacity-70 mb-1">{m.sender_name}</div>}
        {m.image_b64 && <img src={m.image_b64} alt="img" className="max-w-full sm:max-w-xs rounded-sm mb-2 cursor-zoom-in" onClick={() => onZoom(m.image_b64)} />}
        {vid && <VideoBlock url={vid} mine={mine} linkInText={!m.video_url} />}
        {m.text && <div className="whitespace-pre-wrap text-sm break-words">{renderText(m.text)}</div>}
        <div className={`text-[10px] mt-1 ${mine ? "text-[#101114]/70" : "text-[#A8A8AD]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
      </div>
    </div>
  );
}

export default function MessageList({ convId, msgs, userId, jumpSignal }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const seen = useRef({ conv: null, len: 0 });
  const [atBottom, setAtBottom] = useState(true);
  const [unseen, setUnseen] = useState(0);
  const [zoom, setZoom] = useState(null);

  const toBottom = (smooth) => {
    const el = ref.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    setUnseen(0);
  };

  useLayoutEffect(() => {
    if (msgs.conv !== convId) { return; }
    const s = seen.current;
    if (s.conv !== convId) { seen.current = { conv: convId, len: msgs.list.length }; toBottom(false); return; }
    if (msgs.list.length > s.len) {
      setUnseen((n) => n + msgs.list.length - s.len);
      const el = ref.current;
      if (el) setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 48);
    }
    s.len = msgs.list.length;
  }, [msgs, convId]);

  useEffect(() => { if (jumpSignal) { toBottom(true); } }, [jumpSignal]);

  const onScroll = () => {
    const el = ref.current;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    setAtBottom(bottom);
    if (bottom) setUnseen(0);
  };

  return (
    <div className="relative flex-1 min-h-0">
      <div ref={ref} onScroll={onScroll} data-testid="chat-messages-scroll" className="h-full overflow-y-auto p-4 space-y-3 [overflow-anchor:none]">
        {msgs.conv === convId && msgs.list.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === userId} onZoom={setZoom} />)}
      </div>
      {zoom && <Lightbox src={zoom} onClose={() => setZoom(null)} />}
      {!atBottom && (
        <button data-testid="chat-latest-btn" onClick={() => toBottom(true)}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#101114]/90 backdrop-blur border border-[#D4AF37] text-[#F2D98D] hover:bg-[#D4AF37] hover:text-[#101114] transition-colors rounded-full px-4 py-1.5 text-xs font-semibold shadow-lg">
          <ArrowDown size={14} /> {t("chat.latest")}
          {unseen > 0 && <span data-testid="chat-unseen-count" className="bg-[#D4AF37] text-[#101114] rounded-full px-1.5 text-[10px]">{unseen}</span>}
        </button>
      )}
    </div>
  );
}
